import { useEffect, useMemo, useState } from "react";
import { TablePager } from "../components/TablePager";
import { useCustom, useTranslate } from "@refinedev/core";
import {
  PlusCircleSolidIcon,
  Button,
  CheckboxField,
  FormCard,
  SearchIcon,
  SectionHeader,
  StatusBadge,
  type Column,
  type SortState,
} from "@fawkes/ui";
import { LegacyUserMigrationDialog } from "./LegacyUserMigrationDialog";
import { TeamsBeheer } from "./TeamsBeheer";
import { IndeelbareTabel } from "../components/IndeelbareTabel";

export interface AdminUser {
  id: string;
  username: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  active: boolean;
  employeeId?: string;
  removedAt?: string;
  hasPassword: boolean;
  /** An admin set this password and flagged it — the user must replace it at next login. */
  mustChangePassword?: boolean;
}

interface AdminUserPage {
  rows: AdminUser[];
  total: number;
}

/** Server page size — kept in sync with the api default (AdminUserService.DEFAULT_PAGE_SIZE). */
const PAGE_SIZE = 25;

// Admin users list — React migration of the Jmix `users` screen, over the api/ /api/users surface
// (ROLE_USER_ADMIN). Unlike the provisioning list this shows ALL accounts; the "toon verwijderde"
// toggle re-queries with includeRemoved=true (the server param), mirroring the Jmix "show removed" filter.
//
// Search, sort and paging are all SERVER-side: the debounced query, the column sort and the page
// number go out as `?q=&sort=&dir=&page=&size=` and the endpoint answers `{ rows, total }`. The url is
// RELATIVE — the dataProvider prepends `/api` once (a leading `/` would double to /api/api).
export function UserList({
  onOpen,
  onCreate,
}: { onOpen?: (id: string) => void; onCreate?: () => void } = {}) {
  const t = useTranslate();
  const [showRemoved, setShowRemoved] = useState(false);
  const [migrationOpen, setMigrationOpen] = useState(false);
  const [teamsOpen, setTeamsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");
  const [sort, setSort] = useState<SortState>({ key: "username", dir: "asc" });
  const [page, setPage] = useState(1);

  // Debounce the search box into the server query; a new search resets to the first page so the user
  // never lands on an out-of-range page (e.g. typing while on page 5 of the unfiltered list).
  useEffect(() => {
    const handle = setTimeout(() => {
      setDebounced(query.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(handle);
  }, [query]);

  const url = useMemo(() => {
    const params = new URLSearchParams();
    if (debounced) params.set("q", debounced);
    params.set("page", String(page));
    params.set("size", String(PAGE_SIZE));
    params.set("sort", sort.key);
    params.set("dir", sort.dir);
    params.set("includeRemoved", String(showRemoved));
    return `users?${params.toString()}`;
  }, [debounced, page, sort, showRemoved]);

  const { data, isLoading, isError, refetch } = useCustom<AdminUserPage>({ url, method: "get" });
  const rows = data?.data?.rows ?? [];
  const total = data?.data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const from = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const to = Math.min(page * PAGE_SIZE, total);

  // Re-sorting or toggling "toon verwijderde" invalidates the current page number the same way a new
  // search does — page 5 of one ordering is not page 5 of another.
  const changeSort = (next: SortState) => {
    setSort(next);
    setPage(1);
  };

  const fullName = (u: AdminUser) => [u.firstName, u.lastName].filter(Boolean).join(" ") || "—";

  const columns: Column<AdminUser>[] = [
    { header: t("userListView.column.username"), cell: (u) => u.username, sortKey: "username" },
    { header: t("userListView.column.name"), cell: fullName, sortKey: "name" },
    { header: t("userListView.column.email"), cell: (u) => u.email || "—", sortKey: "email" },
    {
      header: t("userListView.column.status"),
      sortKey: "status",
      cell: (u) =>
        u.removedAt ? (
          <StatusBadge tone="danger">{t("userListView.status.removed")}</StatusBadge>
        ) : (
          <StatusBadge tone={u.active ? "success" : "neutral"}>
            {u.active ? t("userListView.status.active") : t("userListView.status.disabled")}
          </StatusBadge>
        ),
    },
  ];

  if (onOpen) {
    columns.push({
      header: "",
      cell: (u) => (
        <Button variant="secondary" onClick={() => onOpen(u.id)}>
          {t("common.open")}
        </Button>
      ),
    });
  }

  return (
    <FormCard>
      <SectionHeader>{t("userListView.title")}</SectionHeader>
      <div className="fawkes-list-toolbar">
        <div className="fawkes-products-search">
          <SearchIcon className="fawkes-products-search__icon" width={16} height={16} />
          <input
            type="search"
            // De placeholder noemt "gebruikersnaam", en daar gaat de wachtwoordkluis van de
            // browser op af: die vulde hier de opgeslagen inlognaam in. Een veldnaam die niet
            // naar inloggen ruikt houdt het zoekveld leeg, samen met autoComplete.
            name="gebruikers-filter"
            autoComplete="off"
            className="fawkes-products-search__input"
            placeholder={t("userListView.search.placeholder")}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label={t("userListView.search.placeholder")}
          />
        </div>
        {onCreate && (
          <Button variant="primary" onClick={onCreate}>
            <PlusCircleSolidIcon width={18} height={18} /> {t("userListView.createButton")}
          </Button>
        )}
        <Button variant="secondary" onClick={() => setMigrationOpen(true)}>
          {t("userListView.migrateLegacyButton")}
        </Button>
        <Button variant="secondary" onClick={() => setTeamsOpen(true)}>
          {t("teamsBeheer.knop")}
        </Button>
        <CheckboxField
          label={t("userListView.showRemoved")}
          checked={showRemoved}
          onChange={(e) => {
            setShowRemoved(e.target.checked);
            setPage(1);
          }}
        />
      </div>
      {isLoading ? (
        <p className="fawkes-muted">{t("common.loading")}</p>
      ) : isError ? (
        <p className="fawkes-error">{t("userListView.loadFailed")}</p>
      ) : (
        <>
          <IndeelbareTabel tabelKey="user-list"
            columns={columns}
            rows={rows}
            getRowKey={(u) => u.id}
            empty={t("userListView.empty")}
            sort={sort}
            onSortChange={changeSort}
          />
          <TablePager
            page={page}
            pageCount={pageCount}
            total={total}
            from={from}
            to={to}
            pageSize={PAGE_SIZE}
            onPage={setPage}
            labels={{
              range: t("userListView.pagination.range"),
              previous: t("userListView.pagination.previous"),
              next: t("userListView.pagination.next"),
              pageSize: t("pager.pageSize"),
              page: t("pager.page"),
            }}
          />
        </>
      )}
      {teamsOpen && <TeamsBeheer onSluit={() => setTeamsOpen(false)} />}
      {migrationOpen && (
        <LegacyUserMigrationDialog
          onClose={() => setMigrationOpen(false)}
          onMigrated={() => refetch()}
        />
      )}
    </FormCard>
  );
}
